import { useEffect, useMemo, useState } from 'react';

import { Copy01Icon, Delete02Icon, Mail01Icon, PencilEdit01Icon } from '@hugeicons/core-free-icons';
import { EMAIL_TEMPLATE_TYPE_OPTIONS } from 'constants/email-config';
import { getFetchBaseQueryErrorMessage } from 'helpers/api';
import { useToast } from 'hooks/toast';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';
import { EmailTemplateModal } from 'components/EmailTemplateModal/EmailTemplateModal';
import { Button, SearchInput, Select } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { IconButton } from 'components/IconButton/IconButton';
import StatusPill from 'components/Pills/StatusPills';
import { Table } from 'components/Table/Table';

import { getBrands, selectBrands } from 'api/brands-slice';
import {
  useCreateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useDuplicateEmailTemplateMutation,
  useGetEmailTemplatesQuery,
  useUpdateEmailTemplateMutation,
} from 'api/email-templates-api';

const CATEGORY_DOT_COLOR = {
  candidate: 'bg-green-2',
  referee: 'bg-yellow-2',
};

const brandNamesForIds = (brandIds, brandsList) => {
  if (!brandIds?.length) {
    return [];
  }
  const byId = new Map(brandsList.map(b => [Number(b.id), b.name]));
  return (brandIds || []).map(id => byId.get(Number(id)) ?? `Brand #${id}`);
};

export const EmailTemplates = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showToast = useToast();
  const brands = useSelector(selectBrands);

  useEffect(() => {
    if (!brands.length) {
      dispatch(getBrands());
    }
  }, [brands.length, dispatch]);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const queryParams = useMemo(() => {
    const p = {};
    if (categoryFilter) p.category = categoryFilter;
    if (searchQuery.trim()) p.search = searchQuery.trim();
    return p;
  }, [categoryFilter, searchQuery]);

  const { data: templates = [], isLoading, isFetching } = useGetEmailTemplatesQuery(queryParams);
  const [createEmailTemplate, { isLoading: isCreating }] = useCreateEmailTemplateMutation();
  const [updateEmailTemplate, { isLoading: isUpdating }] = useUpdateEmailTemplateMutation();
  const [deleteEmailTemplate] = useDeleteEmailTemplateMutation();
  const [duplicateEmailTemplate] = useDuplicateEmailTemplateMutation();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);

  const hasActiveFilters = Boolean(categoryFilter || searchQuery.trim());

  const openCreate = () => {
    setEditingTemplate(null);
    setModalVisible(true);
  };

  const openEdit = template => {
    setEditingTemplate(template);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingTemplate(null);
  };

  const handleSubmit = async data => {
    const result = editingTemplate
      ? await updateEmailTemplate({ id: editingTemplate.id, ...data })
      : await createEmailTemplate(data);
    if (result.error) {
      const detail = getFetchBaseQueryErrorMessage(result.error);
      showToast(
        t('emailTemplates.saveError'),
        detail || t('emailTemplates.errors.saveFailed'),
        'error',
      );
      return;
    }
    showToast(
      t('emailTemplates.saveSuccess'),
      t('emailTemplates.saveSuccessDescription'),
      'success',
    );
    closeModal();
  };

  const handleDuplicate = async id => {
    const result = await duplicateEmailTemplate(id);
    if (result.error) {
      showToast(t('common.error'), t('emailTemplates.errors.duplicateFailed'), 'error');
    }
  };

  const handleDelete = async () => {
    const result = await deleteEmailTemplate(deletingTemplateId);
    if (result.error) {
      showToast(t('common.error'), t('emailTemplates.errors.deleteFailed'), 'error');
    }
    setDeletingTemplateId(null);
  };

  const eventTypeLabel = template => {
    const opt = EMAIL_TEMPLATE_TYPE_OPTIONS.find(o => o.value === template.type);
    return opt?.label ?? '—';
  };

  const showInitialEmpty = !isLoading && templates.length === 0 && !hasActiveFilters;
  const showFilteredEmpty = !isLoading && templates.length === 0 && hasActiveFilters;

  return (
    <div className="flex flex-col gap-8">
      <Alert
        show={!!deletingTemplateId}
        title={t('emailTemplates.delete.title')}
        message={t('emailTemplates.delete.message')}
        ok={t('common.delete')}
        cancel={t('common.cancel')}
        type="error"
        onOk={handleDelete}
        onCancel={() => setDeletingTemplateId(null)}
      />

      <EmailTemplateModal
        visible={modalVisible}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialValues={editingTemplate}
        isLoading={isCreating || isUpdating}
        brands={brands || []}
      />

      <div className="flex flex-col gap-6 bg-neutral-1 p-8 border rounded-3xl border-neutral-4">
        <div className="flex gap-4 items-center">
          <IconComponent icon={Mail01Icon} />
          <div className="flex flex-col gap-1">
            <span className="text-3xl font-medium text-neutral-7">{t('emailTemplates.title')}</span>
            <p className="text-xl text-text-tertiary">{t('emailTemplates.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              placeholder={t('emailTemplates.filters.searchPlaceholder')}
              value={searchQuery}
              onChange={setSearchQuery}
              className="min-w-[200px] flex-1 max-w-md"
            />
            <Select
              name="emailTemplateCategoryFilter"
              className="w-80 min-w-[12rem]"
              value={categoryFilter}
              inputProps={{ onChange: e => setCategoryFilter(e.target.value) }}
              placeholder={t('emailTemplates.filters.allAudience')}>
              <Select.Item value="">{t('emailTemplates.filters.allAudience')}</Select.Item>
              <Select.Item value="candidate">{t('emailTemplates.category.candidate')}</Select.Item>
              <Select.Item value="referee">{t('emailTemplates.category.referee')}</Select.Item>
            </Select>
          </div>
          <Button type="primary" onClick={openCreate}>
            {t('emailTemplates.createButton')}
          </Button>
        </div>
      </div>

      {(isLoading || isFetching) && templates.length === 0 && (
        <p className="text-sm text-neutral-5">{t('common.loading')}</p>
      )}

      {showInitialEmpty && (
        <p className="text-sm text-neutral-5 mt-4">{t('emailTemplates.empty')}</p>
      )}

      {showFilteredEmpty && (
        <p className="text-sm text-neutral-5 mt-4">{t('emailTemplates.noResults')}</p>
      )}

      {!isLoading && templates.length > 0 && (
        <div>
          <Table.Context>
            <Table.TableContent className="overflow-x-auto">
              <Table>
                <Table.Head>
                  <tr>
                    <Table.Header noBorder>{t('emailTemplates.table.name')}</Table.Header>
                    <Table.Header noBorder>{t('emailTemplates.table.audience')}</Table.Header>
                    <Table.Header noBorder>{t('emailTemplates.table.event')}</Table.Header>
                    <Table.Header noBorder>{t('emailTemplates.table.brands')}</Table.Header>
                    <Table.Header noBorder>{t('emailTemplates.table.updated')}</Table.Header>
                    <Table.Header noBorder align="end">
                      {t('emailTemplates.table.actions')}
                    </Table.Header>
                  </tr>
                </Table.Head>
                <Table.Body>
                  {templates.map(template => {
                    const isLinkedToBrand = template.brand_ids?.length > 0;
                    const linkedBrandNames = brandNamesForIds(template.brand_ids, brands || []);
                    const brandsTooltip =
                      linkedBrandNames.length > 0
                        ? linkedBrandNames.join('\n')
                        : t('emailTemplates.table.brandsTooltipNone');
                    return (
                      <Table.Row key={template.id}>
                        <Table.Col className="text-xl font-medium text-neutral-7">
                          <div className="flex flex-col">
                            <span>{template.name}</span>
                            <span className="text-lg font-medium text-text-tertiary max-w-lg truncate inline-block align-bottom">
                              Subject: {template.subject}
                            </span>
                          </div>
                        </Table.Col>
                        <Table.Col>
                          <StatusPill
                            status="neutral"
                            text={
                              template.category === 'candidate'
                                ? t('emailTemplates.category.candidate')
                                : t('emailTemplates.category.referee')
                            }
                            colorDot={CATEGORY_DOT_COLOR[template.category] || 'bg-neutral-4'}
                            className="px-4"
                          />
                        </Table.Col>
                        <Table.Col>
                          <span className="text-xl font-medium text-neutral-6">
                            {eventTypeLabel(template)}
                          </span>
                        </Table.Col>
                        <Table.Col>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`email-template-brands-${template.id}`}>
                                <span className="text-neutral-1 block max-w-xs text-start whitespace-pre-wrap">
                                  {brandsTooltip}
                                </span>
                              </Tooltip>
                            }>
                            <span className="inline-flex cursor-default">
                              <span
                                className={`${template.brand_ids?.length > 0 ? 'bg-primary-1 text-neutral-7' : 'bg-neutral-4 text-text-tertiary'} text-xl font-medium rounded-full w-10 h-10 flex items-center justify-center`}>
                                {template?.brand_ids?.length ?? 0}
                              </span>
                            </span>
                          </OverlayTrigger>
                        </Table.Col>
                        <Table.Col>
                          <span className="text-xl font-medium text-neutral-6 whitespace-nowrap">
                            <Moment fromNow>{template.updated_at || template.created_at}</Moment>
                          </span>
                        </Table.Col>
                        <Table.Col align="right">
                          <div className="d-flex align-items-center justify-content-end">
                            <IconButton
                              className="u-margin-right--small"
                              icon={PencilEdit01Icon}
                              prefix="hugeicons"
                              tip={t('common.edit')}
                              onClick={() => openEdit(template)}
                              placement="top"
                            />
                            <IconButton
                              className="u-margin-right--small"
                              icon={Copy01Icon}
                              prefix="hugeicons"
                              tip={t('emailTemplates.table.duplicate')}
                              onClick={() => handleDuplicate(template.id)}
                              placement="top"
                            />
                            <IconButton
                              icon={Delete02Icon}
                              prefix="hugeicons"
                              tip={
                                isLinkedToBrand
                                  ? t('emailTemplates.delete.linkedToBrand')
                                  : t('common.delete')
                              }
                              onClick={() => setDeletingTemplateId(template.id)}
                              placement="top"
                              type="delete"
                              disabled={isLinkedToBrand}
                            />
                          </div>
                        </Table.Col>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </Table.TableContent>
          </Table.Context>
        </div>
      )}
    </div>
  );
};
